import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { 
  Play,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Beaker,
  Scale,
  List,
  Target,
  Eye,
  ChevronRight,
  Trash2
} from 'lucide-react';
import { formatQuantidade, formatQuantidadeComUnidade, parseNumero, sanitizarEntradaDecimalBr, numeroParaEntradaBr } from '../utils/numberFormat';
import { rotuloUnidade } from '../utils/unidadeMedida';
import { alocarConsumoFEFO, disponivelNaUnidadeFormulacao } from '../utils/consumoFefo';

/** Monta o rascunho local (mesma lógica de criarOrdemProducao no backend), sem gravar no Firestore. */
function buildRascunhoOrdemLocal(produto, numeroBateladas) {
  const nb = parseInt(numeroBateladas, 10);
  const formulacao_planejada = (produto.formulacao || []).map((item) => ({
    materia_prima_id: item.materia_prima_id,
    materia_prima_nome: item.materia_prima_nome,
    quantidade_planejada_min: item.quantidade_minima * nb,
    quantidade_planejada_max: item.quantidade_maxima * nb,
    unidade: item.unidade
  }));
  const processo_execucao = (produto.processo_fabricacao || []).map((etapa) => ({
    ordem: etapa.ordem,
    etapa_nome: etapa.nome,
    etapa_descricao: etapa.descricao,
    validacoes_coletadas: (etapa.validacoes || []).map((val) => ({
      nome: val.nome,
      valor_esperado: val.valor_esperado,
      valor_minimo: val.valor_minimo ?? null,
      valor_maximo: val.valor_maximo ?? null,
      valor_coletado: null,
      conforme: null
    })),
    concluida: false
  }));
  return {
    produto_id: produto.id,
    produto_nome: produto.nome_produto,
    numero_bateladas: nb,
    formulacao_planejada,
    processo_execucao,
    lote_fabricacao: null
  };
}

function formatarDataProducao(val) {
  if (val == null || val === '') return 'N/A';
  try {
    let d;
    if (typeof val === 'object' && val !== null) {
      if (typeof val.toDate === 'function') d = val.toDate();
      else if (typeof val.seconds === 'number') d = new Date(val.seconds * 1000);
      else if (typeof val._seconds === 'number') d = new Date(val._seconds * 1000);
      else if (val instanceof Date) d = val;
      else return 'N/A';
    } else if (typeof val === 'string') d = new Date(val);
    else if (typeof val === 'number') d = new Date(val);
    else return 'N/A';
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
}

/** Data de fabricação/validade do lote de MP (ISO, Timestamp ou {_seconds}). */
function formatarDataSomenteDia(val) {
  if (val == null || val === '') return '—';
  try {
    let d;
    if (typeof val === 'object' && val !== null) {
      if (typeof val.toDate === 'function') d = val.toDate();
      else if (typeof val._seconds === 'number') d = new Date(val._seconds * 1000);
      else if (typeof val.seconds === 'number') d = new Date(val.seconds * 1000);
      else return '—';
    } else if (typeof val === 'string') d = new Date(val);
    else return '—';
    if (!d || Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
}

function tituloLoteFabricacaoWizard(ordem) {
  if (!ordem) return 'Nova Ordem de Produção';
  const lote = ordem.lote_fabricacao;
  if (lote != null && String(lote).trim() !== '' && String(lote).toLowerCase() !== 'null') {
    return `Ordem de Produção — Lote ${lote}`;
  }
  return 'Ordem de Produção — Rascunho (lote ao confirmar)';
}

/** Coluna Lote: `lote_fabricacao` (DDD/AAAA/L); ordens antigas sem lote na pesagem mostram — ou número/ano. */
function textoLoteListaProducao(ordem) {
  const lf = ordem?.lote_fabricacao;
  if (lf != null && String(lf).trim() !== '' && String(lf).toLowerCase() !== 'null') {
    return lf;
  }
  if (ordem?.fase_operacional === 'aguardando_pesagem') {
    return '—';
  }
  const n = ordem?.numero_lote;
  const a = ordem?.ano_fabricacao;
  if (n != null && String(n).trim() !== '' && a != null && String(a).trim() !== '') {
    return `${n}/${a}`;
  }
  return '—';
}

/** Ordens planejadas prontas para o Tempo Real (pesagem feita ou fluxo sem separação). */
function podeIniciarProducaoNaLista(ordem) {
  if (!ordem || ordem.status !== 'planejada') return false;
  return ordem.fase_operacional !== 'aguardando_pesagem';
}

const ProducaoPage = () => {
  const { hasPermission, getEmpresaId, isRole } = useAuth();
  const [ordens, setOrdens] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limpando, setLimpando] = useState(false);
  const [limpezaMsg, setLimpezaMsg] = useState('');
  const [limpezaErro, setLimpezaErro] = useState('');

  // Wizard
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: produto, 2: consumo, 3: processo, 4: finalizar
  
  // Wizard data
  const [produtos, setProdutos] = useState([]);
  const [selectedProduto, setSelectedProduto] = useState(null);
  const [numeroBateladas, setNumeroBateladas] = useState(1);
  /** Rascunho da ordem (passos 2–4); só vira documento no Firestore ao confirmar no passo 4. */
  const [ordemAtual, setOrdemAtual] = useState(null);
  const [consumosPendentes, setConsumosPendentes] = useState([]);
  /** Por índice da etapa (0-based): objeto parametros_validados para registrarValidacaoEtapa */
  const [validacoesEtapasPendentes, setValidacoesEtapasPendentes] = useState([]);
  /** Erros exibidos dentro do modal do wizard (criar ordem, lotes, etc.) */
  const [wizardError, setWizardError] = useState('');
  /** Gravação única no final (criar + iniciar + consumos + etapas) */
  const [salvandoOrdem, setSalvandoOrdem] = useState(false);

  /** Modal visualizar ordem na lista */
  const [ordemDetalhe, setOrdemDetalhe] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [erroDetalhe, setErroDetalhe] = useState('');
  const [iniciandoOrdemId, setIniciandoOrdemId] = useState(null);

  useEffect(() => {
    loadOrdens();
    loadStats();
  }, []);

  const loadOrdens = async () => {
    try {
      setLoading(true);
      const listOrdensProducao = httpsCallable(functions, 'listOrdensProducao');
      const result = await listOrdensProducao({
        empresa_id: getEmpresaId()
      });

      if (result.data.success) {
        setOrdens(result.data.data.items);
      }
    } catch (err) {
      console.error('Erro ao carregar ordens:', err);
    } finally {
      setLoading(false);
    }
  };

  const podeLimpar = isRole('superadmin') || isRole('admin') || isRole('administrador');

  const handleLimparProducao = async () => {
    setLimpezaMsg('');
    setLimpezaErro('');
    const confirmacao = window.prompt(
      'ATENÇÃO: isto apaga TODAS as ordens, carrinhos, apontamentos, cargas de forno, não conformidades e planejamentos de produção da sua empresa. O estoque NÃO é alterado.\n\nDigite ZERAR para confirmar:'
    );
    if (confirmacao == null) return;
    if (confirmacao.trim().toUpperCase() !== 'ZERAR') {
      setLimpezaErro('Confirmação inválida. Nada foi apagado.');
      return;
    }
    try {
      setLimpando(true);
      const limparDadosProducao = httpsCallable(functions, 'limparDadosProducao');
      const r = await limparDadosProducao({ empresa_id: getEmpresaId(), confirmar: true });
      const total = r.data?.data?.total ?? 0;
      setLimpezaMsg(`Dados de produção zerados: ${total} documento(s) removido(s).`);
      await loadOrdens();
      await loadStats();
    } catch (err) {
      console.error('Erro ao limpar dados de produção:', err);
      setLimpezaErro(err.message || 'Erro ao limpar dados de produção.');
    } finally {
      setLimpando(false);
    }
  };

  const abrirDetalheOrdem = async (ordem) => {
    setErroDetalhe('');
    setOrdemDetalhe(null);
    setLoadingDetalhe(true);
    try {
      const getOrdemProducao = httpsCallable(functions, 'getOrdemProducao');
      const result = await getOrdemProducao({
        ordem_producao_id: ordem.id
      });
      if (result.data?.success && result.data?.data) {
        setOrdemDetalhe(result.data.data);
      } else {
        setErroDetalhe('Não foi possível carregar os dados da ordem.');
      }
    } catch (err) {
      console.error('Erro ao buscar ordem:', err);
      setErroDetalhe(err?.message || 'Erro ao carregar a ordem de produção.');
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const fecharDetalheOrdem = () => {
    setOrdemDetalhe(null);
    setErroDetalhe('');
  };

  const handleIniciarProducaoOrdem = async (ordem) => {
    const loteLabel = textoLoteListaProducao(ordem);
    const ok = window.confirm(
      `Iniciar o processamento da ordem ${loteLabel}?\n\nA ordem passará para "Em andamento" e ficará disponível em Tempo Real.`
    );
    if (!ok) return;

    setIniciandoOrdemId(ordem.id);
    try {
      const iniciarProducao = httpsCallable(functions, 'iniciarProducao');
      const result = await iniciarProducao({ ordem_producao_id: ordem.id });
      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Não foi possível iniciar a produção.');
      }
      setOrdemDetalhe(null);
      setErroDetalhe('');
      await loadOrdens();
      await loadStats();
    } catch (err) {
      console.error('Erro ao iniciar produção:', err);
      const msg =
        err?.message ||
        err?.details ||
        'Erro ao iniciar a produção. Verifique se a pesagem foi concluída.';
      alert(msg);
    } finally {
      setIniciandoOrdemId(null);
    }
  };

  const loadStats = async () => {
    try {
      const getProducaoStats = httpsCallable(functions, 'getProducaoStats');
      const result = await getProducaoStats({
        empresa_id: getEmpresaId()
      });

      if (result.data.success) {
        setStats(result.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const loadProdutos = async () => {
    try {
      const listProductionProducts = httpsCallable(functions, 'listProductionProducts');
      const result = await listProductionProducts({
        empresa_id: getEmpresaId(),
        apenas_ativos: true
      });

      if (result.data.success) {
        setProdutos(result.data.data.items);
      }
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    }
  };

  const handleIniciarNovaProducao = () => {
    setShowWizard(true);
    setCurrentStep(1);
    setSelectedProduto(null);
    setNumeroBateladas(1);
    setOrdemAtual(null);
    setConsumosPendentes([]);
    setValidacoesEtapasPendentes([]);
    setWizardError('');
    loadProdutos();
  };

  /** Passo 1 → 2: só monta rascunho local (nada é salvo no servidor). */
  const handleAvancarPasso1 = () => {
    if (!selectedProduto || numeroBateladas <= 0) {
      setWizardError('Selecione um produto e informe o número de bateladas');
      return;
    }
    setWizardError('');
    const draft = buildRascunhoOrdemLocal(selectedProduto, numeroBateladas);
    setOrdemAtual(draft);
    setConsumosPendentes([]);
    setValidacoesEtapasPendentes([]);
    setCurrentStep(2);
  };

  /** Passo 4: persiste tudo de uma vez (criar ordem → iniciar → consumos → etapas → finalizar se necessário). */
  const handleSalvarProducaoCompleta = async () => {
    if (!ordemAtual) return;
    setWizardError('');
    setSalvandoOrdem(true);
    let ordemId = null;
    try {
      const criarOrdemProducao = httpsCallable(functions, 'criarOrdemProducao');
      const criar = await criarOrdemProducao({
        empresa_id: getEmpresaId(),
        produto_id: ordemAtual.produto_id,
        numero_bateladas: ordemAtual.numero_bateladas
      });
      if (!criar.data.success) {
        throw new Error('Falha ao criar ordem de produção');
      }
      ordemId = criar.data.data.id;

      const iniciarProducao = httpsCallable(functions, 'iniciarProducao');
      let iniciou = false;
      let lastErr = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 800));
          await iniciarProducao({ ordem_producao_id: ordemId });
          iniciou = true;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!iniciou) {
        throw new Error(
          lastErr?.message || lastErr?.details || 'Falha ao iniciar a produção'
        );
      }

      const registrarConsumoMateriaPrima = httpsCallable(
        functions,
        'registrarConsumoMateriaPrima'
      );
      for (const c of consumosPendentes) {
        const r = await registrarConsumoMateriaPrima({
          empresa_id: getEmpresaId(),
          ordem_producao_id: ordemId,
          materia_prima_id: c.materia_prima_id,
          recebimento_id: c.recebimento_id,
          quantidade_utilizada: c.quantidade
        });
        if (!r.data.success) {
          throw new Error('Falha ao registrar consumo de matéria-prima');
        }
      }

      const nEtapas = ordemAtual.processo_execucao?.length || 0;
      // Não chamar registrarValidacaoEtapa aqui: cada chamada conclui a etapa e, na última,
      // marca a ordem como concluída — a tela Tempo Real só lista em_andamento/pausada.
      // As etapas devem ser executadas e validadas em Tempo Real (ou outra tela de operação).

      if (nEtapas === 0) {
        const finalizarProducao = httpsCallable(functions, 'finalizarProducao');
        const fr = await finalizarProducao({ ordem_producao_id: ordemId });
        if (!fr.data.success) {
          throw new Error('Falha ao finalizar produção');
        }
      }

      setShowWizard(false);
      setOrdemAtual(null);
      setConsumosPendentes([]);
      setValidacoesEtapasPendentes([]);
      setCurrentStep(1);
      await loadOrdens();
      await loadStats();
    } catch (err) {
      console.error('Erro ao salvar produção completa:', err);
      const msg = err?.message || err?.details || String(err);
      if (ordemId) {
        try {
          const cancelarProducao = httpsCallable(functions, 'cancelarProducao');
          await cancelarProducao({
            ordem_producao_id: ordemId,
            motivo:
              'Cancelamento automático: falha ao concluir registro da produção (dados parciais).'
          });
        } catch (ce) {
          console.error('Falha ao cancelar ordem após erro:', ce);
          setWizardError(
            `${msg} — Ordem ${ordemId} pode precisar de ajuste manual (estoque já pode ter sido alterado).`
          );
          await loadOrdens();
          setSalvandoOrdem(false);
          return;
        }
        setWizardError(
          `${msg} A ordem criada foi cancelada. Se algum consumo já tiver sido registrado, verifique o estoque.`
        );
      } else {
        setWizardError(msg);
      }
      await loadOrdens();
    } finally {
      setSalvandoOrdem(false);
    }
  };

  const getStatusBadge = (ordem) => {
    const status = typeof ordem === 'string' ? ordem : ordem?.status;
    const fase = typeof ordem === 'object' ? ordem?.fase_operacional : null;
    const badges = {
      planejada: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Planejada' },
      em_andamento: { color: 'bg-yellow-100 text-yellow-800', icon: Play, label: 'Em Andamento' },
      concluida: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Concluída' },
      cancelada: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelada' },
      perda: { color: 'bg-gray-800 text-white', icon: XCircle, label: 'Perda' }
    };

    const badge = badges[status] || badges.planejada;
    const Icon = badge.icon;
    let label = badge.label;
    if (status === 'planejada' && fase === 'aguardando_pesagem') {
      label = 'Não iniciada';
    }
    if (status === 'planejada' && fase === 'pesagem_concluida') {
      label = 'Pesagem concluída';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {label}
      </span>
    );
  };

  if (!hasPermission('manage_production')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso Negado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Você não tem permissão para acessar o módulo de produção.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ordens de Produção</h1>
          <p className="text-gray-600">
            Consulta das ordens. As ordens do dia são criadas no Planejamento e processadas pelo fluxo de
            Separação → Lista de trabalho dos setores.
          </p>
        </div>
        {podeLimpar && (
          <button
            type="button"
            onClick={handleLimparProducao}
            disabled={limpando}
            title="Apaga ordens, carrinhos, apontamentos, cargas, não conformidades e planejamentos. Não altera o estoque."
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {limpando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Zerar dados de produção
          </button>
        )}
      </div>

      {limpezaMsg && (
        <div className="rounded-lg bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
          {limpezaMsg}
        </div>
      )}
      {limpezaErro && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">
          {limpezaErro}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <Package className="h-8 w-8 text-gray-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Planejadas</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.planejadas}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Em Andamento</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.em_andamento}</p>
              </div>
              <Play className="h-8 w-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Concluídas</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.concluidas}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Bateladas</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{stats.total_bateladas}</p>
              </div>
              <Target className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Lista de Ordens */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2">Carregando ordens...</span>
        </div>
      ) : ordens.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma ordem de produção</h3>
          <p className="mt-1 text-sm text-gray-500">
            Inicie sua primeira ordem de produção.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Lote
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Bateladas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {ordens.map((ordem) => (
                <tr key={ordem.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm font-bold text-blue-600">
                      {textoLoteListaProducao(ordem)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{ordem.produto_nome}</div>
                    <div className="text-sm text-gray-500">{ordem.produto_versao}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {ordem.numero_bateladas}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(ordem)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatarDataProducao(ordem.criado_em)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => abrirDetalheOrdem(ordem)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                      title="Visualizar detalhes"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: detalhe da ordem */}
      {(loadingDetalhe || ordemDetalhe || erroDetalhe) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {ordemDetalhe
                  ? `Ordem — Lote ${ordemDetalhe.lote_fabricacao || '—'}`
                  : loadingDetalhe
                    ? 'Carregando...'
                    : 'Detalhe da ordem'}
              </h3>
              <button
                type="button"
                onClick={fecharDetalheOrdem}
                className="text-gray-500 hover:text-gray-800 p-1"
                aria-label="Fechar"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {loadingDetalhe && (
                <div className="flex items-center justify-center py-12 gap-2 text-gray-600">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span>Carregando ordem...</span>
                </div>
              )}
              {erroDetalhe && !loadingDetalhe && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
                  {erroDetalhe}
                </div>
              )}
              {ordemDetalhe && !loadingDetalhe && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Produto</span>
                      <p className="font-medium text-gray-900">{ordemDetalhe.produto_nome}</p>
                      <p className="text-gray-500 text-xs">{ordemDetalhe.produto_versao}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status</span>
                      <p className="mt-0.5">{getStatusBadge(ordemDetalhe)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Bateladas</span>
                      <p className="font-medium">{ordemDetalhe.numero_bateladas}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Criado em</span>
                      <p className="font-medium">{formatarDataProducao(ordemDetalhe.criado_em)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Iniciado em</span>
                      <p className="font-medium">{formatarDataProducao(ordemDetalhe.iniciado_em)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Concluído em</span>
                      <p className="font-medium">{formatarDataProducao(ordemDetalhe.concluido_em)}</p>
                    </div>
                  </div>

                  {Array.isArray(ordemDetalhe.formulacao_real) && ordemDetalhe.formulacao_real.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <Beaker className="h-4 w-4" />
                        Consumo real (ingredientes)
                      </h4>
                      <ul className="text-sm border rounded divide-y max-h-48 overflow-y-auto">
                        {ordemDetalhe.formulacao_real.map((item, i) => (
                          <li key={i} className="px-3 py-2 flex justify-between gap-2">
                            <span>{item.materia_prima_nome}</span>
                            <span className="text-gray-600 whitespace-nowrap">
                              {formatQuantidadeComUnidade(item.quantidade_real_utilizada, item.unidade)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {Array.isArray(ordemDetalhe.etapas_executadas) &&
                    ordemDetalhe.etapas_executadas.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                          <List className="h-4 w-4" />
                          Etapas executadas ({ordemDetalhe.etapas_executadas.length})
                        </h4>
                        <ul className="text-sm border rounded divide-y max-h-56 overflow-y-auto">
                          {ordemDetalhe.etapas_executadas.map((e, i) => (
                            <li key={i} className="px-3 py-2">
                              <span className="font-medium">
                                {e.numero_ordem ?? e.ordem}. {e.nome ?? e.etapa_nome}
                              </span>
                              <span className="text-gray-500 text-xs ml-2">
                                {e.status || '—'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {(!ordemDetalhe.etapas_executadas ||
                    ordemDetalhe.etapas_executadas.length === 0) &&
                    Array.isArray(ordemDetalhe.processo_execucao) &&
                    ordemDetalhe.processo_execucao.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">
                          Processo (planejado / legado)
                        </h4>
                        <ul className="text-sm border rounded divide-y max-h-48 overflow-y-auto">
                          {ordemDetalhe.processo_execucao.map((e, i) => (
                            <li key={i} className="px-3 py-2">
                              {e.ordem}. {e.etapa_nome || e.nome}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </>
              )}
            </div>
            <div className="px-6 py-3 border-t bg-gray-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={fecharDetalheOrdem}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wizard */}
      {showWizard && (
        <WizardProducao
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          produtos={produtos}
          selectedProduto={selectedProduto}
          setSelectedProduto={setSelectedProduto}
          numeroBateladas={numeroBateladas}
          setNumeroBateladas={setNumeroBateladas}
          ordemAtual={ordemAtual}
          consumosPendentes={consumosPendentes}
          setConsumosPendentes={setConsumosPendentes}
          validacoesEtapasPendentes={validacoesEtapasPendentes}
          setValidacoesEtapasPendentes={setValidacoesEtapasPendentes}
          wizardError={wizardError}
          setWizardError={setWizardError}
          salvandoOrdem={salvandoOrdem}
          onClose={() => {
            setShowWizard(false);
            setWizardError('');
            setOrdemAtual(null);
            setConsumosPendentes([]);
            setValidacoesEtapasPendentes([]);
            loadOrdens();
            loadStats();
          }}
          onAvancarPasso1={handleAvancarPasso1}
          onSalvarProducaoCompleta={handleSalvarProducaoCompleta}
        />
      )}
    </div>
  );
};

// ==================== WIZARD DE PRODUÇÃO ====================

const WizardProducao = ({
  currentStep,
  setCurrentStep,
  produtos,
  selectedProduto,
  setSelectedProduto,
  numeroBateladas,
  setNumeroBateladas,
  ordemAtual,
  consumosPendentes,
  setConsumosPendentes,
  validacoesEtapasPendentes,
  setValidacoesEtapasPendentes,
  wizardError,
  setWizardError,
  salvandoOrdem,
  onClose,
  onAvancarPasso1,
  onSalvarProducaoCompleta
}) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {tituloLoteFabricacaoWizard(ordemAtual)}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[
              { step: 1, label: 'Produto', icon: Package },
              { step: 2, label: 'Pesagem', icon: Scale },
              { step: 3, label: 'Processo', icon: List },
              { step: 4, label: 'Finalizar', icon: CheckCircle }
            ].map(({ step, label, icon: Icon }) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep >= step ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  {label}
                </span>
                {step < 4 && (
                  <div className={`w-16 h-1 mx-4 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {wizardError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {wizardError}
          </div>
        )}

        {/* Step Content */}
        {currentStep === 1 && (
          <Step1Produto
            produtos={produtos}
            selectedProduto={selectedProduto}
            setSelectedProduto={setSelectedProduto}
            numeroBateladas={numeroBateladas}
            setNumeroBateladas={setNumeroBateladas}
            onNext={onAvancarPasso1}
            onCancel={onClose}
            loading={false}
          />
        )}

        {currentStep === 2 && ordemAtual && (
          <Step2ConsumoMPs
            ordemAtual={ordemAtual}
            consumosPendentes={consumosPendentes}
            setConsumosPendentes={setConsumosPendentes}
            onNext={() => {
              const n = ordemAtual.processo_execucao?.length || 0;
              setValidacoesEtapasPendentes(Array.from({ length: n }, () => ({})));
              setCurrentStep(n === 0 ? 4 : 3);
            }}
            onBack={() => setCurrentStep(1)}
            setError={setWizardError}
          />
        )}

        {currentStep === 3 && ordemAtual && (
          <Step3Processo
            ordemAtual={ordemAtual}
            validacoesEtapasPendentes={validacoesEtapasPendentes}
            setValidacoesEtapasPendentes={setValidacoesEtapasPendentes}
            onNext={() => setCurrentStep(4)}
            onBack={() => setCurrentStep(2)}
            setError={setWizardError}
          />
        )}

        {currentStep === 4 && ordemAtual && (
          <Step4Finalizar
            ordemAtual={ordemAtual}
            consumosPendentes={consumosPendentes}
            onFinish={onSalvarProducaoCompleta}
            onBack={() => {
              const n = ordemAtual.processo_execucao?.length || 0;
              setCurrentStep(n === 0 ? 2 : 3);
            }}
            loading={salvandoOrdem}
          />
        )}
      </div>
    </div>
  );
};

// ==================== STEP 1: SELECIONAR PRODUTO ====================

const Step1Produto = ({ produtos, selectedProduto, setSelectedProduto, numeroBateladas, setNumeroBateladas, onNext, onCancel, loading }) => {
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          Passo 1: Selecionar Produto
        </h4>
        <p className="text-sm text-blue-700">
          Escolha o produto que será fabricado e informe quantas bateladas serão produzidas.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Produto *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {produtos.map((produto) => (
              <div
                key={produto.id}
                onClick={() => setSelectedProduto(produto)}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedProduto?.id === produto.id
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">{produto.nome_produto}</h4>
                    {produto.codigo ? (
                      <p className="text-sm font-mono text-gray-600 mt-0.5">{produto.codigo}</p>
                    ) : null}
                    <p className="text-sm text-gray-500 mt-1">{produto.versao}</p>
                    <div className="mt-2 flex gap-4 text-xs text-gray-600">
                      <span>{produto.formulacao?.length || 0} MPs</span>
                      <span>{produto.processo_fabricacao?.length || 0} etapas</span>
                    </div>
                  </div>
                  {selectedProduto?.id === produto.id && (
                    <CheckCircle className="h-6 w-6 text-blue-600" />
                  )}
                </div>
              </div>
            ))}
          </div>
          {produtos.length === 0 && (
            <p className="text-sm text-yellow-600">
              ⚠️ Nenhum produto ativo encontrado. Cadastre um produto em Produtos primeiro.
            </p>
          )}
        </div>

        {selectedProduto && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número de Bateladas *
            </label>
            <input
              type="number"
              min="1"
              value={numeroBateladas}
              onChange={(e) => setNumeroBateladas(e.target.value)}
              className="w-full md:w-64 border border-gray-300 rounded-md px-3 py-2"
              placeholder="1"
            />
            <p className="text-sm text-gray-500 mt-1">
              O sistema calculará automaticamente as quantidades necessárias de ingredientes.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Cancelar
        </button>
        <button
          onClick={onNext}
          disabled={!selectedProduto || numeroBateladas <= 0 || loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Aguarde...
            </>
          ) : (
            <>
              Próximo: pesagem
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ==================== STEP 2: PESAGEM (consumo de MPs) ====================

function lotesAposConsumoPendente(lotes, consumosPendentes, materiaPrimaId) {
  const forMp = consumosPendentes.filter((c) => c.materia_prima_id === materiaPrimaId);
  return (lotes || []).map((l) => {
    const used = forMp
      .filter((c) => c.recebimento_id === l.recebimento_id)
      .reduce((s, c) => s + (Number(c.quantidade) || 0), 0);
    return {
      ...l,
      quantidade_disponivel: Math.max(0, (Number(l.quantidade_disponivel) || 0) - used)
    };
  });
}

const Step2ConsumoMPs = ({
  ordemAtual,
  consumosPendentes,
  setConsumosPendentes,
  onNext,
  onBack,
  setError
}) => {
  const { getEmpresaId } = useAuth();
  const [lotesDisponiveis, setLotesDisponiveis] = useState({});

  useEffect(() => {
    ordemAtual.formulacao_planejada.forEach((mp) => {
      loadLotesDisponiveis(mp.materia_prima_id);
    });
  }, []);

  const loadLotesDisponiveis = async (materiaPrimaId) => {
    try {
      const getLotesDisponiveis = httpsCallable(functions, 'getLotesDisponiveis');
      const result = await getLotesDisponiveis({
        empresa_id: getEmpresaId(),
        materia_prima_id: materiaPrimaId
      });

      if (result.data.success) {
        setLotesDisponiveis((prev) => ({
          ...prev,
          [materiaPrimaId]: result.data.data.lotes || []
        }));
      } else {
        setError('Não foi possível carregar os lotes em estoque para um dos ingredientes.');
      }
    } catch (err) {
      console.error('Erro ao carregar lotes:', err);
      const detalhe = err?.message || err?.details || '';
      setError(
        detalhe
          ? `Erro ao carregar lotes: ${detalhe}. Verifique se as funções de estoque foram publicadas (getLotesDisponiveis).`
          : 'Erro ao carregar lotes em estoque. Publique as Cloud Functions de estoque ou tente novamente.'
      );
    }
  };

  const handleRegistrarConsumoPlano = (mpIndex, plano) => {
    const mp = ordemAtual.formulacao_planejada[mpIndex];
    if (!Array.isArray(plano) || plano.length === 0) return;
    setConsumosPendentes((prev) => [
      ...prev,
      ...plano.map(({ lote, quantidade }) => ({
        materia_prima_id: mp.materia_prima_id,
        materia_prima_nome: mp.materia_prima_nome,
        quantidade,
        recebimento_id: lote.recebimento_id,
        lote_fornecedor: lote.lote_fornecedor,
        fornecedor_nome: lote.fornecedor_nome
      }))
    ]);
  };

  return (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-green-900 mb-2">
          Passo 2: Pesagem
        </h4>
        <p className="text-sm text-green-700">
          Informe a quantidade a pesar (vírgula decimal). O sistema indica quanto pegar de cada lote (FEFO). Os dados
          ficam neste rascunho até o último passo; nada é gravado no servidor agora.
        </p>
      </div>

      <div className="space-y-6">
        {ordemAtual.formulacao_planejada.map((mp, index) => (
          <ConsumoMPCard
            key={index}
            mp={mp}
            lotes={lotesAposConsumoPendente(
              lotesDisponiveis[mp.materia_prima_id],
              consumosPendentes,
              mp.materia_prima_id
            )}
            onRegistrarPlano={(plano) => handleRegistrarConsumoPlano(index, plano)}
            consumosRegistrados={consumosPendentes.filter(
              (c) => c.materia_prima_id === mp.materia_prima_id
            )}
            loading={false}
          />
        ))}
      </div>

      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
        <button
          onClick={onNext}
          disabled={consumosPendentes.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          Próximo: Processo
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// ==================== CARD DE CONSUMO DE MP ====================

const ConsumoMPCard = ({ mp, lotes, onRegistrarPlano, consumosRegistrados, loading }) => {
  const totalConsumido = consumosRegistrados.reduce((sum, c) => sum + c.quantidade, 0);
  const minPlanejado = Number(mp.quantidade_planejada_min) || 0;
  const maxPlanejado = Number(mp.quantidade_planejada_max) || 0;
  const faltaMin = Math.max(0, minPlanejado - totalConsumido);
  const tetoMax = Math.max(0, maxPlanejado - totalConsumido);
  const minimoAtingido = totalConsumido + 1e-9 >= minPlanejado;
  const maximoAtingido = totalConsumido + 1e-9 >= maxPlanejado;

  const [adicionarMais, setAdicionarMais] = useState(false);
  const [quantidadeStr, setQuantidadeStr] = useState(() => numeroParaEntradaBr(faltaMin > 0 ? faltaMin : ''));

  useEffect(() => {
    if (!minimoAtingido) {
      setQuantidadeStr(numeroParaEntradaBr(faltaMin > 0 ? faltaMin : ''));
      setAdicionarMais(false);
    } else {
      setQuantidadeStr('');
    }
  }, [mp.materia_prima_id, minimoAtingido, faltaMin]);

  const mostrarFormPesagem = !minimoAtingido || (adicionarMais && !maximoAtingido);
  const podeAdicionarMais = minimoAtingido && !maximoAtingido && lotes.length > 0;

  const quantidadeAlvo = parseNumero(quantidadeStr);
  const { plano, faltante } = useMemo(
    () =>
      alocarConsumoFEFO(
        lotes,
        Number.isFinite(quantidadeAlvo) && quantidadeAlvo > 0 ? quantidadeAlvo : 0,
        mp.unidade
      ),
    [lotes, quantidadeAlvo, mp.unidade]
  );
  const qtdPorRecebimento = useMemo(() => {
    const m = new Map();
    plano.forEach(({ lote, quantidade }) => {
      m.set(lote.recebimento_id, quantidade);
    });
    return m;
  }, [plano]);

  const podeRegistrar =
    mostrarFormPesagem &&
    plano.length > 0 &&
    Number.isFinite(quantidadeAlvo) &&
    quantidadeAlvo > 0 &&
    faltante < 1e-6 &&
    (tetoMax <= 0 || quantidadeAlvo <= tetoMax + 1e-6);

  return (
    <div
      className={`border-2 rounded-lg p-4 ${
        minimoAtingido ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-300 bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-medium text-gray-900">{mp.materia_prima_nome}</h4>
          <p className="text-sm text-gray-600 mt-1">
            Planejado:{' '}
            <span className="font-semibold">
              {formatQuantidade(mp.quantidade_planejada_min)} - {formatQuantidadeComUnidade(mp.quantidade_planejada_max, mp.unidade)}
            </span>
          </p>
          <p className="text-sm text-gray-600">
            Consumido:{' '}
            <span className="font-semibold text-blue-600">
              {formatQuantidadeComUnidade(totalConsumido, mp.unidade)}
            </span>
          </p>
          {faltaMin > 0 && (
            <p className="text-sm text-yellow-600 font-medium">
              Falta (mín.): {formatQuantidadeComUnidade(faltaMin, mp.unidade)}
            </p>
          )}
          {minimoAtingido && (
            <p className="text-sm text-emerald-800 font-medium mt-1">
              Mínimo atendido
              {!maximoAtingido && tetoMax > 0
                ? ` (ainda é possível adicionar até ${formatQuantidadeComUnidade(tetoMax, mp.unidade)}).`
                : '.'}
            </p>
          )}
        </div>
        {minimoAtingido && <CheckCircle className="h-6 w-6 text-green-600" />}
      </div>

      {minimoAtingido && !mostrarFormPesagem && (
        <div className="space-y-3">
          {podeAdicionarMais ? (
            <button
              type="button"
              className="text-sm text-emerald-800 underline underline-offset-2 hover:text-emerald-950"
              onClick={() => {
                setAdicionarMais(true);
                setQuantidadeStr('');
              }}
              disabled={loading}
            >
              Adicionar mais quantidade (até o máximo)
            </button>
          ) : maximoAtingido ? (
            <p className="text-xs text-gray-500">Quantidade máxima atingida.</p>
          ) : (
            <p className="text-xs text-gray-500">
              Sem saldo adicional em estoque. O consumo já registrado permanece válido.
            </p>
          )}
        </div>
      )}

      {mostrarFormPesagem && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantidade a pesar ({rotuloUnidade(mp.unidade)})
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={quantidadeStr}
              onChange={(e) => setQuantidadeStr(sanitizarEntradaDecimalBr(e.target.value))}
              className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 bg-white"
              placeholder="Ex.: 10,5"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Use vírgula como decimal. O sistema indica quanto retirar de cada lote (FEFO).
            </p>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            <span>
              Lotes disponíveis
              {lotes.length > 0 ? ` — ${lotes.length} lote(s)` : ''}:
            </span>
            {lotes.length > 0 ? (
              <span className="block font-normal text-gray-500 mt-1 text-xs">
                Ordenados por validade (FEFO). Destaque = quantidade a pegar deste lote.
              </span>
            ) : null}
          </label>

          {lotes.length === 0 ? (
            <p className="text-sm text-red-600">Nenhum lote disponível em estoque</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {lotes.map((lote) => {
                const qConsumir = qtdPorRecebimento.get(lote.recebimento_id);
                const noPlano = qConsumir != null && qConsumir > 0;
                return (
                  <div
                    key={lote.recebimento_id || lote.lote_fornecedor}
                    className={`border rounded p-3 transition-all ${
                      noPlano ? 'border-emerald-600 bg-emerald-50' : 'border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-900">
                          Lote:{' '}
                          <span className="font-mono">
                            {lote.lote_fornecedor != null && String(lote.lote_fornecedor).trim() !== ''
                              ? lote.lote_fornecedor
                              : '—'}
                          </span>
                        </p>
                        <p className="text-xs text-gray-600">Fornecedor: {lote.fornecedor_nome || '—'}</p>
                        <p className="text-xs text-gray-600">
                          Fabricação:{' '}
                          <span className="font-medium text-gray-800">{formatarDataSomenteDia(lote.data_fabricacao)}</span>
                          {' · '}
                          Validade:{' '}
                          <span className="font-medium text-gray-800">{formatarDataSomenteDia(lote.data_validade)}</span>
                        </p>
                        <p className="text-xs text-gray-600">
                          Disponível:{' '}
                          <span className="font-semibold">
                            {formatQuantidadeComUnidade(
                              lote.quantidade_disponivel,
                              rotuloUnidade(lote.unidade || mp.unidade)
                            )}
                          </span>
                          {(() => {
                            const uLote = rotuloUnidade(lote.unidade || mp.unidade);
                            const uForm = rotuloUnidade(mp.unidade);
                            if (uLote.toLowerCase() === uForm.toLowerCase()) return null;
                            const equiv = disponivelNaUnidadeFormulacao(lote, mp.unidade);
                            return (
                              <span className="text-gray-500">
                                {' '}
                                (≈ {formatQuantidadeComUnidade(equiv, uForm)})
                              </span>
                            );
                          })()}
                        </p>
                      </div>
                      {noPlano ? (
                        <div className="shrink-0 text-right">
                          <p className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">Pegar</p>
                          <p className="text-sm font-bold text-emerald-800">
                            {formatQuantidadeComUnidade(qConsumir, mp.unidade)}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {Number.isFinite(quantidadeAlvo) && quantidadeAlvo > 0 && faltante > 1e-6 && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              Estoque insuficiente: faltam {formatQuantidadeComUnidade(faltante, mp.unidade)} além dos lotes disponíveis.
            </p>
          )}

          {plano.length > 0 && (
            <div className="bg-white border border-emerald-200 rounded-md px-3 py-2 text-xs text-gray-700 space-y-1">
              <p className="font-medium text-emerald-900">Plano de consumo (FEFO):</p>
              {plano.map(({ lote, quantidade }, idx) => (
                <p key={lote.recebimento_id || idx}>
                  {formatQuantidadeComUnidade(quantidade, mp.unidade)} do lote{' '}
                  <span className="font-mono">
                    {lote.lote_fornecedor != null && String(lote.lote_fornecedor).trim() !== ''
                      ? lote.lote_fornecedor
                      : '—'}
                  </span>
                </p>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                if (!podeRegistrar) return;
                onRegistrarPlano(plano);
              }}
              disabled={loading || !podeRegistrar}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              Registrar conforme indicado
            </button>
            {adicionarMais && (
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setAdicionarMais(false);
                  setQuantidadeStr('');
                }}
                disabled={loading}
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {consumosRegistrados.length > 0 && (
        <div className="mt-4 bg-white rounded border p-3">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Consumos Registrados:</h5>
          {consumosRegistrados.map((c, idx) => (
            <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
              <CheckCircle className="h-3 w-3 text-green-600" />
              {formatQuantidadeComUnidade(c.quantidade, mp.unidade)} - Lote {c.lote_fornecedor} ({c.fornecedor_nome})
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== STEP 3: PROCESSO ====================

const Step3Processo = ({
  ordemAtual,
  validacoesEtapasPendentes,
  setValidacoesEtapasPendentes,
  onNext,
  onBack,
  setError
}) => {
  const [etapaAtual, setEtapaAtual] = useState(0);
  const [validacoes, setValidacoes] = useState({});

  const etapa = ordemAtual.processo_execucao[etapaAtual];

  useEffect(() => {
    setValidacoes(validacoesEtapasPendentes[etapaAtual] || {});
  }, [etapaAtual, validacoesEtapasPendentes]);

  const handleSalvarEtapa = () => {
    try {
      const parametros_validados = { ...validacoes };
      setValidacoesEtapasPendentes((prev) => {
        const next = [...prev];
        next[etapaAtual] = parametros_validados;
        return next;
      });

      if (etapaAtual < ordemAtual.processo_execucao.length - 1) {
        setEtapaAtual((i) => i + 1);
      } else {
        onNext();
      }
    } catch (err) {
      console.error('Erro ao salvar etapa:', err);
      setError('Erro ao salvar etapa');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-purple-900 mb-2">
          Passo 3: Registrar Processo de Fabricação
        </h4>
        <p className="text-sm text-purple-700">
          Revise as etapas e, se quiser, anote valores de referência — é só rascunho neste assistente. O registro
          oficial dos parâmetros e o avanço das etapas são feitos em <strong>Tempo Real</strong> após criar a ordem.
        </p>
      </div>

      {/* Progresso das Etapas */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {ordemAtual.processo_execucao.map((e, idx) => (
          <div key={idx} className="flex items-center">
            <div
              className={`px-3 py-2 rounded text-sm font-medium whitespace-nowrap ${
                idx === etapaAtual
                  ? 'bg-blue-600 text-white'
                  : idx < etapaAtual
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {e.etapa_nome}
            </div>
            {idx < ordemAtual.processo_execucao.length - 1 && (
              <ChevronRight className="h-4 w-4 text-gray-400 mx-1" />
            )}
          </div>
        ))}
      </div>

      {/* Etapa Atual */}
      <div className="border-2 border-blue-300 rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
            {etapa.ordem}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{etapa.etapa_nome}</h3>
            <p className="text-sm text-gray-600">{etapa.etapa_descricao}</p>
          </div>
        </div>

        {/* Validações */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 mb-3">Validações:</h4>
          {etapa.validacoes_coletadas.map((val, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {val.nome}
                </label>
                <div className="text-xs text-gray-500 mb-1">
                  Esperado:{' '}
                  <span className="font-medium">
                    {val.valor_minimo != null &&
                    val.valor_minimo !== '' &&
                    val.valor_maximo != null &&
                    val.valor_maximo !== ''
                      ? `${val.valor_minimo} – ${val.valor_maximo}`
                      : val.valor_esperado || '—'}
                  </span>
                </div>
                <input
                  type="text"
                  value={validacoes[val.nome] || ''}
                  onChange={(e) => setValidacoes(prev => ({ ...prev, [val.nome]: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Valor coletado..."
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
        <button
          onClick={handleSalvarEtapa}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {etapaAtual < ordemAtual.processo_execucao.length - 1 ? (
            <>
              Próxima Etapa
              <ArrowRight className="h-4 w-4" />
            </>
          ) : (
            <>
              Finalizar Processo
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ==================== STEP 4: FINALIZAR ====================

const Step4Finalizar = ({ ordemAtual, consumosPendentes, onFinish, onBack, loading }) => {
  const nEtapas = ordemAtual.processo_execucao?.length || 0;
  return (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-green-900 mb-2">
          Passo 4: Confirmar e gravar no sistema
        </h4>
        <p className="text-sm text-green-700">
          Somente agora a ordem será criada no servidor e os consumos de ingredientes aplicados. Se fechar o
          assistente antes deste passo, nada é salvo.
        </p>
        {nEtapas > 0 && (
          <p className="text-sm text-green-800 mt-2 font-medium">
            Com etapas de processo: após confirmar, a ordem fica <strong>em andamento</strong> e aparece em{' '}
            <strong>Tempo Real</strong> para registrar cada etapa (parâmetros e tempos) na linha de produção.
          </p>
        )}
      </div>

      {/* Resumo */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Resumo (rascunho)</h3>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-600">Lote de fabricação:</p>
            <p className="text-lg font-mono font-bold text-blue-600">
              {ordemAtual.lote_fabricacao || '— gerado ao confirmar —'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Produto:</p>
            <p className="text-lg font-medium text-gray-900">{ordemAtual.produto_nome}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Bateladas:</p>
            <p className="text-lg font-medium text-gray-900">{ordemAtual.numero_bateladas}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Registros de consumo:</p>
            <p className="text-lg font-medium text-gray-900">{consumosPendentes.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Etapas de processo:</p>
            <p className="text-lg font-medium text-gray-900">{nEtapas}</p>
          </div>
        </div>

        <div className="flex items-center justify-center p-8 bg-amber-50 rounded-lg border-2 border-amber-200">
          <div className="text-center">
            <Package className="h-12 w-12 text-amber-700 mx-auto mb-3" />
            <h4 className="text-lg font-bold text-gray-900 mb-2">Pronto para gravar</h4>
            <p className="text-gray-600 text-sm">
              {nEtapas > 0
                ? 'Ao confirmar: ordem criada, produção iniciada e consumos gravados. As etapas você conclui em Tempo Real.'
                : 'Ao confirmar: ordem criada, consumos gravados e produção finalizada (produto sem etapas de processo).'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onBack}
          disabled={loading}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
        <button
          onClick={onFinish}
          disabled={loading}
          className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-lg font-medium"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Gravando produção...
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5" />
              Confirmar e salvar produção
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ProducaoPage;

